import React, { useState, useEffect } from "react";
import { IconPhotoOff } from "@tabler/icons-react";

const API_BASE_URL = process.env.REACT_APP_API_URL 
    ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') 
    : "https://api.nearzon.com";

export const getImageUrl = (url) => {
    if (!url) return null;
    let normalizedUrl = url.toString().replace(/\\/g, '/');
    if (normalizedUrl.startsWith("http") || normalizedUrl.startsWith("data:")) return normalizedUrl;
    
    if (normalizedUrl.startsWith('/')) {
        normalizedUrl = normalizedUrl.slice(1);
    }
    
    if (!normalizedUrl.startsWith('uploads/')) {
        normalizedUrl = `uploads/${normalizedUrl}`;
    }
    
    try {
        // Strip out ANY leading slashes from the relative path
        const relativePath = normalizedUrl.replace(/^\/+/, "");
        
        // Ensure base URL ends with exactly one slash
        const baseUrl = `${API_BASE_URL.replace(/\/+$/, "")}/`;
        
        // Use new URL to safely construct the full address without double slashes
        return new URL(relativePath, baseUrl).toString();
    } catch (e) {
        // Fallback for environments with invalid base URLs
        const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
        return `${baseUrl}/${normalizedUrl.replace(/^\/+/, "")}`;
    }
};

const SafeImage = ({ src, fallbackSrc, alt, className, style, placeholderText, variant, ...props }) => {
    const [error, setError] = useState(false);
    const [useFallback, setUseFallback] = useState(false);

    useEffect(() => {
        setError(false);
        setUseFallback(false);
    }, [src, fallbackSrc]);

    const finalSrc = getImageUrl(useFallback && fallbackSrc ? fallbackSrc : src);

    // Dynamic class calculation based on variant
    const getVariantClass = () => {
        switch (variant) {
            case 'square': return 'img-square';
            case 'landscape': return 'img-landscape';
            case 'banner': return 'img-banner';
            case 'thumbnail': return 'img-thumbnail';
            case 'preview': return 'img-preview-form';
            default: return '';
        }
    };

    const combinedClassName = `${className || ''} ${getVariantClass()}`.trim();

    const renderPlaceholderText = (text) => {
        if (!text) return "No Image";
        if (typeof text === 'string') return text;
        if (typeof text === 'object') return text.name || JSON.stringify(text);
        return String(text);
    };

    if (error || !finalSrc) {
        const safePlaceholder = renderPlaceholderText(placeholderText);
        return (
            <div
                className={`${combinedClassName} safe-image-placeholder pcu-bg-muted pcu-color-muted pcu-flex-column pcu-flex-center pcu-text-center pcu-border-primary-light pcu-overflow-hidden`}
                style={{
                    ...style,
                }}
                title={safePlaceholder}
            >
                <IconPhotoOff size={24} className="pcu-margin-bottom-4 pcu-opacity-50" />
                <span className="pcu-font-10 pcu-line-height-1-2 pcu-padding-x-4">
                    {safePlaceholder}
                </span>
            </div>
        );
    }

    return (
        <img
            src={finalSrc}
            alt={alt || "Image"}
            className={combinedClassName}
            style={style}
            onError={() => {
                if (!useFallback && fallbackSrc) {
                    setUseFallback(true);
                } else {
                    setError(true);
                }
            }}
            {...props}
        />
    );
};

export default SafeImage;
